import type { FlowStage, StageGlyph, StageStatus } from '@interfaces/IFlowView';
import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  PackageIcon,
  ShieldIcon,
  TruckIcon,
  WrenchIcon,
} from '@utils/icons';
import styles from './LifecycleStages.module.css';

type ILifecycleStagesProps = {
  title: string;
  stages: FlowStage[];
  activeKey: StageGlyph;
  onSelect: (key: StageGlyph) => void;
  statusLabels: Record<StageStatus, string>;
};

function StageIcon({ glyph }: { glyph: StageGlyph }) {
  switch (glyph) {
    case 'order':    return <PackageIcon size={16} />;
    case 'deliver':  return <TruckIcon size={16} />;
    case 'schedule': return <CalendarIcon size={16} />;
    case 'install':  return <WrenchIcon size={16} />;
    case 'verify':   return <ShieldIcon size={16} />;
  }
}

const STATUS_ORDER: StageStatus[] = ['complete', 'in-progress', 'delayed', 'pending'];

export function LifecycleStages({ title, stages, activeKey, onSelect, statusLabels }: ILifecycleStagesProps) {
  return (
    <section className={styles.panel}>
      <header className={styles.head}>
        <div className={styles.heading}>
          <WrenchIcon size={16} />
          <h2 className={styles.title}>{title}</h2>
        </div>
        <ul className={styles.legend}>
          {STATUS_ORDER.map((s) => (
            <li key={s} className={styles.legendItem}>
              <span className={styles.legendDot} data-status={s} />
              {statusLabels[s]}
            </li>
          ))}
        </ul>
      </header>

      <div className={styles.row}>
        {stages.map((s) => (
          <button
            type="button"
            key={s.key}
            className={styles.stage}
            data-status={s.status}
            data-active={s.key === activeKey}
            onClick={() => onSelect(s.key)}
          >
            <div className={styles.stageHead}>
              <span className={styles.stageGlyph}>
                <StageIcon glyph={s.glyph} />
              </span>
              <span className={styles.stageIndex}>STAGE {s.index}</span>
              <span className={styles.stageStatusDot} aria-hidden />
            </div>
            <div className={styles.stageBody}>
              <span className={styles.stageTitle}>{s.title}</span>
              <span className={styles.stageOwner}>{s.owner}</span>
            </div>
            <div className={styles.barWrap}>
              <span className={styles.bar} aria-hidden>
                <span className={styles.barFill} style={{ width: `${s.progress}%` }} />
              </span>
              <span className={styles.barValue}>{s.progress}%</span>
            </div>
            <div className={styles.stageFoot}>
              <span className={styles.due}>
                <ClockIcon size={12} />
                {s.dueLabel}
              </span>
              <span className={styles.pill}>
                {s.status === 'complete' && <CheckCircleIcon size={12} />}
                {statusLabels[s.status]}
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
